import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDashboard, purchaseAnalytics } from './api';
import { formatDate } from './client/dates';
import { useChartColors, money, pctChange } from './sales/theme';
import Sparkline from './sales/Sparkline';

// Ordered pipeline stages. `returned` is deliberately not in this list: it is not a
// step forward, it is work bounced back, and it gets its own callout below the bar.
// The old overview computed it server-side and then never rendered it at all.
const STAGES = [
  { key: 'new', label: 'New' },
  { key: 'in_review', label: 'In review' },
  { key: 'approved', label: 'Approved' },
  { key: 'sold', label: 'Sold' },
];

const DAY = 24 * 60 * 60 * 1000;
const isoDaysAgo = (n) => new Date(Date.now() - n * DAY).toISOString().slice(0, 10);
const daysUntil = (date) => Math.round((new Date(`${date}T12:00:00`) - Date.now()) / DAY);

// One row per thing waiting on a human, biggest pile first, and anything at zero
// drops out entirely. Twelve tiles reading "0" is not a to-do list, it is noise you
// learn to skip, which is how the one number that mattered got missed.
function ActionRow({ label, count, hint, tone, onClick }) {
  const urgent = tone === 'urgent';
  return (
    <button type="button" onClick={onClick}
            className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg border border-gray-200
                       dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500
                       hover:bg-blue-50/50 dark:hover:bg-gray-800 transition">
      <span className={`text-xl font-semibold tabular-nums w-10 shrink-0
        ${urgent ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`}>
        {count}
      </span>
      <span className="min-w-0">
        <span className="block text-sm text-gray-800 dark:text-gray-200">{label}</span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
      <span aria-hidden="true" className="ml-auto text-gray-300 dark:text-gray-600">›</span>
    </button>
  );
}

function Stat({ label, value, sub, children }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
      {/* Sparkline spans the tile below the text rather than sitting beside it: at
          this tile width, side by side squeezed the delta onto two lines. */}
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}

// Ordered stages share one hue stepped light to dark, so the bar reads as a single
// path with a direction rather than four unrelated categories competing for
// attention. Validated as an ordinal ramp in both modes.
function PipelineFunnel({ stage, onOpen }) {
  const c = useChartColors();
  const counts = STAGES.map((s, i) => ({ ...s, value: stage[s.key] || 0, color: c.funnel[i] }));
  const total = counts.reduce((sum, s) => sum + s.value, 0);
  const returned = stage.returned || 0;

  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-5" aria-label="Concept pipeline">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold">Concept pipeline</h3>
        <button type="button" onClick={onOpen} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
          Open concepts
        </button>
      </div>

      {total === 0 ? (
        <p className="text-xs text-gray-500">No concepts in flight. They land here once an intake is submitted.</p>
      ) : (
        <>
          <div className="flex h-2.5 rounded-full overflow-hidden mb-3" style={{ background: c.track }}>
            {counts.filter((s) => s.value > 0).map((s, i) => (
              <div key={s.key} title={`${s.label}: ${s.value}`}
                   style={{ width: `${(s.value / total) * 100}%`, background: s.color, marginLeft: i === 0 ? 0 : 2 }} />
            ))}
          </div>
          {/* Count sits against its label, not pushed to the far edge of a grid cell:
              a number stranded across the row reads as belonging to nothing. */}
          <ul className="flex flex-wrap gap-x-6 gap-y-1.5">
            {counts.map((s) => (
              <li key={s.key} className="flex items-center gap-1.5 text-xs">
                <span aria-hidden="true" className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="text-gray-700 dark:text-gray-300">{s.label}</span>
                <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100">{s.value}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {returned > 0 && (
        <p className="text-xs text-orange-600 dark:text-orange-400 mt-3">
          {returned} returned for revision, waiting on an author.
        </p>
      )}
    </section>
  );
}

function ClassesInBuild({ classes }) {
  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-xl p-4" aria-label="Classes in build">
      <h3 className="text-sm font-semibold mb-3">Classes in build</h3>
      <ul className="space-y-1.5">
        {classes.map((c) => {
          const left = c.build_due_on ? daysUntil(c.build_due_on) : null;
          const late = left != null && left < 0;
          return (
            <li key={c.id} className="flex items-baseline gap-2 text-xs">
              <span className="truncate text-gray-700 dark:text-gray-300" title={`${c.program} · ${c.title}`}>
                <span className="text-gray-400">{c.program}</span> {c.title}
              </span>
              {/* The raw ISO date said nothing about urgency. The day count does, and
                  the date stays for the person who needs the actual deadline. */}
              <span className={`ml-auto shrink-0 tabular-nums ${late
                ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-500'}`}>
                {c.build_due_on
                  ? `${late ? `${Math.abs(left)}d overdue` : `${left}d left`} · ${formatDate(c.build_due_on)}`
                  : 'no due date'}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function Dashboard({ onNavigate = () => {} }) {
  const { data: d, isLoading, isError } = useQuery({ queryKey: ['training-dashboard'], queryFn: getDashboard });

  // Directors get the same 90-day window the Sales tab opens on, so the two screens
  // never quote different revenue for the same period.
  const salesRange = { from: isoDaysAgo(89), to: new Date().toISOString().slice(0, 10) };
  const { data: sales } = useQuery({
    queryKey: ['training-purchase-analytics', salesRange],
    queryFn: () => purchaseAnalytics(salesRange),
    enabled: Boolean(d?.is_director), // the endpoint is director-only; do not 403 everyone else
  });
  const c = useChartColors();

  if (isLoading) return <div className="text-sm text-gray-500">Loading overview...</div>;
  if (isError || !d) return <div className="text-sm text-gray-500">Overview unavailable.</div>;

  const stage = d.concepts_by_stage || {};
  const catalog = d.catalog || {};
  const openAdmin = (section) => onNavigate('admin', { section });

  const actions = [
    { key: 'breaches', label: 'SLA breaches on your reviews', count: d.my_reviews?.breached || 0,
      hint: 'past the turnaround you committed to', tone: 'urgent', onClick: () => onNavigate('reviews') },
    { key: 'reviews', label: 'Reviews waiting on you', count: d.my_reviews?.pending || 0,
      hint: 'concepts blocked until you decide', onClick: () => onNavigate('reviews') },
    { key: 'overrides', label: 'Overrides awaiting signature', count: d.overrides_awaiting || 0,
      hint: 'pricing or policy exceptions', tone: 'urgent', onClick: () => onNavigate('concepts') },
    { key: 'publish', label: 'Ready to publish', count: d.ready_to_publish || 0,
      hint: 'produced drafts cleared for release', onClick: () => onNavigate('concepts') },
    { key: 'drafts', label: 'Catalog drafts to confirm', count: catalog.drafts || 0,
      hint: 'synced from SharePoint, never reviewed', onClick: () => openAdmin('catalog') },
    { key: 'sla_all', label: 'SLA breaches across the team', count: d.is_director ? (d.sla_breaches_all || 0) : 0,
      hint: 'everyone, not only you', tone: 'urgent', onClick: () => onNavigate('reviews') },
  ].filter((a) => a.count > 0);

  const revenueDelta = pctChange(sales?.revenue_cents ?? 0, sales?.prior?.revenue_cents);

  return (
    <div>
      <section className="mb-5" aria-label="Needs you">
        <h3 className="text-sm font-semibold mb-2">Needs you</h3>
        {actions.length === 0 ? (
          <p className="text-sm text-gray-500 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl px-4 py-6 text-center">
            Nothing is waiting on you right now.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {actions.map((a) => <ActionRow key={a.key} {...a} />)}
          </div>
        )}
      </section>

      <PipelineFunnel stage={stage} onOpen={() => onNavigate('concepts')} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Confirmed classes" value={catalog.confirmed ?? 0}
              sub={catalog.drafts ? `${catalog.drafts} still in draft` : 'all synced classes reviewed'} />
        <Stat label="Offered to clients" value={catalog.offered ?? 0}
              sub={catalog.offered ? 'buyable in the portal' : 'nothing on sale yet'} />
        {d.is_director && (
          <Stat label="Revenue, 90 days" value={money(sales?.revenue_cents)}
                sub={revenueDelta == null
                  ? `${sales?.count ?? 0} order${sales?.count === 1 ? '' : 's'}`
                  : `${revenueDelta >= 0 ? '▲' : '▼'} ${Math.abs(revenueDelta).toFixed(0)}% vs prior 90d`}>
            <Sparkline values={(sales?.series || []).map((b) => b.revenue_cents)} color={c.series}
                       width={200} height={24} label="Revenue over the last 90 days" />
          </Stat>
        )}
        {d.is_director && (
          <Stat label="Seats claimed"
                value={sales?.seat_utilization == null ? '-' : `${Math.round(sales.seat_utilization * 100)}%`}
                sub={`${sales?.seats_claimed ?? 0} of ${sales?.seats_sold ?? 0} sold seats`} />
        )}
      </div>

      {d.is_director && (
        <button type="button" onClick={() => openAdmin('sales')}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline mb-5 inline-block">
          Open the sales dashboard
        </button>
      )}

      {(d.classes_needing_build || []).length > 0 && <ClassesInBuild classes={d.classes_needing_build} />}
    </div>
  );
}
